import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import deepmerge from 'deepmerge'
import { MpxResponseError } from '../../types'
import { generatePath } from '../../utils'
import { GlobalAuthState } from './GlobalAuthState'

export type ErrorListener = (error: AxiosError) => void

type PathConfig = { template: string; params: Record<string, string> }

const finalPath = (path: string | PathConfig) => {
  switch (typeof path) {
    case 'string':
      return path
    default:
      return generatePath(path.template, path.params)
  }
}

const defaultConfig: AxiosRequestConfig = {
  /**
   * @todo figure out a more appropriate timeout value
   */
  timeout: 60 * 1000,
  headers: {
    'Cache-Control': 'no-cache',
  },
}

export type AgentMiddleware = (config: AxiosRequestConfig) => AxiosRequestConfig

export type AgentOptions = {
  agentConfig?: AxiosRequestConfig
  requestMiddlewares?: AgentMiddleware[]
  onGetResponse?(response: AxiosResponse): void
}

export type SupplementedAxiosError = AxiosError & {
  context_id?: string
  context_path?: string
  pathTemplate?: string
  pathParams?: Record<string, string>
}
export type MpxAxiosError = AxiosResponse<MpxResponseError> & { name?: string }

export const getReleaseName = () => {
  try {
    // @ts-ignore vite puts that there
    return import.meta.env.VITE_RELEASE_NAME
  } catch (e) {
    // @ts-ignore vite puts that there
    return process.env.RELEASE_NAME || 'unknown'
  }
}

const getRequestConfig = () => {
  return {
    headers: {
      CbRequestPath: `release=${getReleaseName()}; timestamp=${Date.now().toString()}`,
    },
  }
}

export const getBaseAgent = (options: AgentOptions = {}) => {
  const agentConfig = options.agentConfig || {}
  const requestMiddlewares = options.requestMiddlewares || []

  const onGetResponse = options.onGetResponse

  const requestConfig = (config?: AxiosRequestConfig) =>
    requestMiddlewares.reduce(
      (latestConfig, middlewareStep) => middlewareStep(latestConfig),
      deepmerge.all([defaultConfig, agentConfig, getRequestConfig(), config || {}])
    )

  // Attach helpful request specifics to the error
  const handleError = (error: SupplementedAxiosError, path: string | PathConfig) => {
    if (error.code === '401') {
      error.name = 'Unauthorized Request'
      GlobalAuthState.clearAuthState()
    }
    if (typeof path === 'string') {
      error.pathTemplate = path
    } else {
      error.pathTemplate = path.template
      error.pathParams = path.params
    }

    error.context_path = `release=${getReleaseName()}; timestamp=${Date.now().toString()}`
    error.context_id = error.response?.headers['x-request-id']

    throw error
  }

  return {
    get: <T>(path: string | PathConfig, config?: AxiosRequestConfig) =>
      axios
        .get<T>(finalPath(path), requestConfig(config))
        .then((response: AxiosResponse) => {
          if (onGetResponse) {
            onGetResponse(response)
          }
          return response as AxiosResponse<T>
        })
        .catch(error => handleError(error, path)),
    put: <T>(path: string | PathConfig, data: any, config?: AxiosRequestConfig) =>
      axios
        .put<T>(finalPath(path), data, requestConfig(config))
        .catch(error => handleError(error, path)),
    patch: <T>(path: string | PathConfig, data: any, config?: AxiosRequestConfig) =>
      axios
        .patch<T>(finalPath(path), data, requestConfig(config))
        .catch(error => handleError(error, path)),

    post: <T>(path: string | PathConfig, data: any = {}, config?: AxiosRequestConfig) =>
      axios
        .post<T>(finalPath(path), data, requestConfig(config))
        .catch(error => handleError(error, path)),

    delete: <T>(path: string | PathConfig, config?: AxiosRequestConfig) =>
      axios
        .delete<T>(finalPath(path), requestConfig(config))
        .catch(error => handleError(error, path)),
  }
}
